/*
  AAA cart reconcile (loaded once from layout/theme.liquid after aaa-cart.js).

  The cart is edited from several places (buy box, drawer, /cart, Appstle's own cart script) and Appstle's discount
  function prices lines by what is in the cart, not by how a line got there. This runs after every cart change and
  makes the cart match the business rules, then reloads the drawer (or the /cart sections):

  1. Free gift (lines with _appstle_bundle_combo_id = progressive_gift): kept only while the jars that earn it are in
     the cart, at most one gift line, quantity 1, on the jars' plan when they are subscribed. A gift that is no longer
     earned is never taken out of the cart: it turns into a paid line in place (a tagged add-on at the add-on price while
     a qualifying jar remains, else a plain line) and the shopper is told; once the jars qualify again that same line turns
     free again in place (Jake, 2026-10-22: no disappearing and reappearing lip balm). When a gift is earned and the shopper already has the same lip balm
     in the cart (untagged, or as a tagged add-on), ONE unit of it becomes the free one and is never also re-added as a
     paid add-on (that double-add was the 2026-10-13 "three lip balm lines" bug).
  2. Add-ons (tagged lines whose variant is in a rule's upsell list): keep the add-on price only while a qualifying
     product of that rule is in the cart; otherwise the bundle tags are removed so the drawer stops promising the
     discount, and the shopper is told. An untagged add-on next to a qualifying product (e.g. lip balm added from its
     own page, then jars) is re-added tagged so it gets the same price the drawer offers; if a tagged line of that
     add-on already exists it absorbs the units instead, and two tagged lines of one add-on under different rules merge
     into the cheaper one, so an add-on is always ONE line at ONE price.
  3. Only line keys are used, and every line is re-resolved from a fresh /cart.js before each change (keys change
     whenever Appstle re-prices a line). Requests carry __internalCall so Appstle's interceptor leaves them alone.

  Rules come from Appstle's bundle_rules (window._ABConfig, else the drawer's JSON block).
*/
(function () {
  if (window.__aaaReconcile) return;
  window.__aaaReconcile = true;

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Appstle-Cart': '1' };
  var MSG_GIFT_LOST = 'Your lip balm is no longer free: the free gift needs two or more jars in the cart. Add another jar and it is free again.';
  var MSG_GIFT_BACK = 'Your lip balm is free again.';
  var MSG_ADDON = 'Add-on pricing applies only when a qualifying product ships with it, so it is now at its regular price.';
  var LOG = '[aaa-reconcile]';

  function parse(x) { if (typeof x !== 'string') return x; try { return JSON.parse(x); } catch (e) { return null; } }
  function nativeFetch() {
    return (window._ABConfig && typeof window._ABConfig.__appstleBundlesNativeFetch === 'function') ? window._ABConfig.__appstleBundlesNativeFetch : window.fetch.bind(window);
  }
  function getCart() {
    return nativeFetch()(root + 'cart.js', { headers: { 'Accept': 'application/json' }, __internalCall: true }).then(function (r) { return r.json(); });
  }
  function post(path, body) {
    return nativeFetch()(root + path, { method: 'POST', headers: HEADERS, body: JSON.stringify(body), __internalCall: true })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); });
  }

  /* ---------- rules ---------- */
  function rawRules() {
    var r = parse(window._ABConfig && window._ABConfig.bundle_rules);
    if (!Array.isArray(r)) {
      var el = document.querySelector('[data-aaa-offers-rules], [data-bb-rules]');
      r = parse(el ? el.textContent : null);
      if (typeof r === 'string') r = parse(r);
    }
    return Array.isArray(r) ? r : [];
  }
  function rules() {
    return rawRules().filter(function (r) {
      return r && r.bundleType === 'VOLUME_DISCOUNT' && (!r.status || r.status === 'ACTIVE') && r.uniqueRef;
    }).map(function (r) {
      var variants = parse(r.variants) || [];
      /* A rule's gift counts only when Appstle has it switched on: the soap & lip balm rule carries a copied gift config with enableProgressiveGifts false, and honouring it added a $10 lip balm to soap carts and ate one balm from a lip balm 3-pack (2026-10-21). */
      var pg = r.enableProgressiveGifts ? parse(r.progressiveGift) : null;
      var gifts = pg && Array.isArray(pg.gifts) ? pg.gifts.filter(function (g) { return g && g.variant && g.variant.variantId; }) : [];
      var tiers = (parse(r.tieredDiscount) || []).filter(function (t) { return t && t.discountBasedOn !== 'AMOUNT' && Number(t.value) > 0; })
        .map(function (t) { return Number(t.value); }).sort(function (a, b) { return a - b; });
      return {
        ref: r.uniqueRef,
        name: r.name || '',
        variantIds: variants.map(function (v) { return String(v.variantId || v.id); }),
        productIds: variants.map(function (v) { return String(v.productId); }).filter(function (p) { return p && p !== 'undefined'; }),
        /* A rule whose add-on list is switched off in Appstle (enableVolumeDiscountUpsell false, e.g. the soap & lip balm rule)
           has no add-ons: Appstle never prices them, so the theme must not tag or promise them (2026-10-23). */
        upsellIds: r.enableVolumeDiscountUpsell === false ? [] : (parse(r.upsells) || []).filter(function (u) { return u && u.variant; }).map(function (u) { return String(u.variant.variantId); }),
        giftIds: gifts.map(function (g) { return String(g.variant.variantId); }),
        giftHandle: gifts.length ? String(gifts[0].variant.productHandle || '') : '',
        giftUnlockQty: gifts.length ? tiers[Math.min(Number(gifts[0].unlockAtTierIndex || 0), tiers.length - 1)] || 0 : 0,
        tiers: tiers
      };
    });
  }

  /* ---------- helpers ---------- */
  function isGift(l) { return !!(l.properties && l.properties._appstle_bundle_combo_id === 'progressive_gift'); }
  function tagOf(l) { return (l.properties && l.properties['_appstle-bb-id']) || ''; }
  /* A line's identity without its key: variant + plan + properties, ignoring the rule's display name (Appstle and the
     theme serve "BUNDLE & SAVE" and "BUNDLE &amp; SAVE" for the same rule). */
  function sig(l) { var p = Object.assign({}, l.properties || {}); delete p['__appstle-bb-name']; return String(l.variant_id) + '|' + (l.selling_plan_allocation ? l.selling_plan_allocation.selling_plan.id : '') + '|' + JSON.stringify(p); }
  function has(list, v) { return list.indexOf(String(v)) > -1; }
  function stripTags(props) {
    var p = Object.assign({}, props || {});
    delete p['_appstle-bb-id']; delete p['__appstle-bb-name']; delete p['_appstle_bundles_type'];
    return p;
  }
  function stripGift(props) { var p = Object.assign({}, props || {}); delete p._appstle_bundle_combo_id; return p; }
  function tagProps(props, rule, extra) {
    return Object.assign({}, props || {}, { '_appstle-bb-id': rule.ref, '__appstle-bb-name': rule.name, '_appstle_bundles_type': 'VOLUME_DISCOUNT' }, extra || {});
  }
  /* Appstle counts the rule's own VARIANTS across every line (gift lines excluded). A travel jar of the same product does
     not count toward the jars' gift: counting by product kept a "free" lip balm alive that Appstle was already charging
     $8 for (Jake, 2026-10-22). */
  function productQty(rule, lines) {
    return lines.filter(function (l) { return !isGift(l) && has(rule.variantIds, l.variant_id); })
      .reduce(function (s, l) { return s + l.quantity; }, 0);
  }
  function giftEarned(rule, lines) { return rule.giftIds.length > 0 && rule.giftUnlockQty > 0 && productQty(rule, lines) >= rule.giftUnlockQty; }
  /* The gift product's plans by variant and plan name (cheapest when a name exists in two groups), loaded once. */
  var GP = {}, GPloading = {};
  function loadGiftPlans(R) {
    return Promise.all(R.filter(function (r) { return r.giftHandle && !GP[r.giftHandle]; }).map(function (r) {
      var h = r.giftHandle;
      if (!GPloading[h]) GPloading[h] = fetch(root + 'products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (p) {
          var names = {};
          (p.selling_plan_groups || []).forEach(function (g) { (g.selling_plans || []).forEach(function (sp) { names[String(sp.id)] = sp.name; }); });
          var map = {};
          (p.variants || []).forEach(function (v) {
            var byName = {};
            (v.selling_plan_allocations || []).forEach(function (a) {
              var nm = names[String(a.selling_plan_id)];
              if (nm && (!byName[nm] || a.price < byName[nm].price)) byName[nm] = { id: a.selling_plan_id, price: a.price };
            });
            map[String(v.id)] = byName;
          });
          GP[h] = map;
        }).catch(function () { GP[h] = {}; });
      return GPloading[h];
    }));
  }
  /* A gift line to add: tagged, one unit, and on the jars' frequency when the jars are subscribed. */
  function giftItem(rule, variantId, lines) {
    var item = { id: Number(variantId), quantity: 1, properties: tagProps(null, rule, { '_appstle_bundle_combo_id': 'progressive_gift' }) };
    var anchor = lines.find(function (a) { return !isGift(a) && has(rule.variantIds, a.variant_id) && a.selling_plan_allocation; });
    var plans = anchor && GP[rule.giftHandle] && GP[rule.giftHandle][String(variantId)];
    var pl = plans && plans[anchor.selling_plan_allocation.selling_plan.name];
    if (pl) item.selling_plan = Number(pl.id);
    return item;
  }
  /* A line that lets an add-on keep its price: any other non-gift line selling one of the rule's own variants. */
  function anchorFor(rule, line, lines) {
    return lines.some(function (a) { return a !== line && !isGift(a) && has(rule.variantIds, a.variant_id); });
  }

  /* ---------- plan one pass: a list of operations ---------- */
  function plan(cart, R) {
    var lines = cart.items || [];
    var ops = [], notes = [];
    var byRef = {};
    R.forEach(function (r) { byRef[r.ref] = r; });

    /* Shopify's change.js ignores an empty properties object, so a line that must lose ALL its properties is replaced
       instead: a plain copy is added first and the tagged line is emptied only once the copy exists (2026-10-22). */
    function replaceProps(l, props) {
      if (Object.keys(props || {}).length) { ops.push({ type: 'change', line: l, quantity: l.quantity, properties: props }); return; }
      var item = { id: l.variant_id, quantity: l.quantity };
      if (l.selling_plan_allocation) item.selling_plan = l.selling_plan_allocation.selling_plan.id;
      var addOp = { type: 'add', item: item };
      ops.push(addOp);
      ops.push({ type: 'change', line: l, quantity: 0, dependsOn: addOp });
    }

    /* 1. gifts */
    var kept = null;
    var consumed = {};   // line key -> units already turned into the gift (so step 2 does not re-add them as a paid add-on)
    var delta = {};      // line key -> quantity change to apply at the end (merges, absorbed units)
    var dependsOn = {};  // line key -> the add op its delta change waits for (skipped when that add failed)
    var zeroAfter = [];  // {key, op}: lines emptied only after the line that absorbed their units was updated
    lines.filter(isGift).forEach(function (l) {
      var rule = byRef[tagOf(l)];
      if (!rule || !giftEarned(rule, lines)) {
        /* Fewer jars than the gift needs: the lip balm stays in the cart and becomes a paid line in place, a tagged
           add-on while a qualifying jar is still in the cart (add-on price), otherwise a plain line at its regular price. */
        var asAddon = !!(rule && has(rule.upsellIds, l.variant_id) && anchorFor(rule, l, lines));
        var paid = asAddon ? tagProps(stripGift(l.properties), rule) : stripTags(stripGift(l.properties));
        replaceProps(l, paid);
        notes.push(MSG_GIFT_LOST);
        return;
      }
      if (kept) { ops.push({ type: 'change', line: l, quantity: 0 }); return; }
      kept = l;
      /* One unit. A subscribed gift keeps its plan: free on this order, then re-priced as an add-on on renewals. */
      if (l.quantity !== 1) ops.push({ type: 'change', line: l, quantity: 1 });
    });
    if (!kept) {
      R.some(function (rule) {
        if (!giftEarned(rule, lines)) return false;
        /* One unit of the gift product already in the cart becomes the free one: an untagged line first, else a tagged add-on line. */
        var src = lines.find(function (l) { return !isGift(l) && !tagOf(l) && has(rule.giftIds, l.variant_id); })
               || lines.find(function (l) { return !isGift(l) && tagOf(l) && has(rule.giftIds, l.variant_id); });
        if (!src) return false;
        var gift = giftItem(rule, src.variant_id, lines);
        var srcPlan = src.selling_plan_allocation ? Number(src.selling_plan_allocation.selling_plan.id) : null;
        var giftPlan = gift.selling_plan ? Number(gift.selling_plan) : null;
        if (src.quantity === 1 && srcPlan === giftPlan) {
          /* One unit on the right plan already (typically the gift that was lost a moment ago): re-tag that line, no flicker. */
          ops.push({ type: 'change', line: src, quantity: 1, properties: gift.properties });
          if (tagOf(src)) notes.push(MSG_GIFT_BACK);
          consumed[src.key] = 1;
          kept = src;
          return true;
        }
        /* Add the gift first; the source line gives up its unit only once the add succeeded (never lose a paid unit). */
        var giftAdd = { type: 'add', item: gift };
        ops.push(giftAdd);
        if (tagOf(src)) { delta[src.key] = (delta[src.key] || 0) - 1; dependsOn[src.key] = giftAdd; }
        else ops.push({ type: 'change', line: src, quantity: src.quantity - 1, dependsOn: giftAdd });
        consumed[src.key] = 1;
        kept = src;
        return true;
      });
    }

    /* 2. add-ons */
    function isAddon(l) { var t = tagOf(l), r = t ? byRef[t] : null; return !!(r && has(r.upsellIds, l.variant_id) && !has(r.variantIds, l.variant_id)); }
    function planId(l) { return l.selling_plan_allocation ? String(l.selling_plan_allocation.selling_plan.id) : ''; }
    /* 2a. the same add-on under two different rule tags (two prices) collapses into one line, the cheaper one */
    var groups = {}, removed = {};
    lines.forEach(function (l) {
      if (isGift(l) || !isAddon(l)) return;
      var k = String(l.variant_id) + '|' + planId(l);
      (groups[k] = groups[k] || []).push(l);
    });
    Object.keys(groups).forEach(function (k) {
      var g = groups[k]; if (g.length < 2) return;
      g.sort(function (a, b) { return a.final_price - b.final_price; });
      var keep = g[0];
      g.slice(1).forEach(function (l) {
        var units = l.quantity - (consumed[l.key] || 0);
        delta[keep.key] = (delta[keep.key] || 0) + Math.max(0, units);
        zeroAfter.push({ key: keep.key, op: { type: 'change', line: l, quantity: 0 } }); removed[l.key] = true; delete delta[l.key];
      });
    });
    /* 2b. tagged add-ons keep their price only next to a qualifying product; untagged add-ons are tagged (or absorbed by an existing tagged line) */
    lines.forEach(function (l) {
      if (isGift(l) || removed[l.key]) return;
      var remaining = l.quantity - (consumed[l.key] || 0);
      if (remaining <= 0) return;
      var tag = tagOf(l), rule = tag ? byRef[tag] : null;
      if (tag && rule && isAddon(l)) {
        if (!anchorFor(rule, l, lines)) { replaceProps(l, stripTags(l.properties)); notes.push(MSG_ADDON); }
        return;
      }
      if (tag) return;
      /* untagged: prefer the rule of a tagged qualifying line, then a rule with a gift, then any */
      var candidates = R.filter(function (r) { return has(r.upsellIds, l.variant_id) && !has(r.variantIds, l.variant_id) && anchorFor(r, l, lines); });
      if (!candidates.length) return;
      var existing = lines.find(function (a) { return a !== l && !isGift(a) && !removed[a.key] && isAddon(a) && String(a.variant_id) === String(l.variant_id) && planId(a) === planId(l); });
      if (existing) { delta[existing.key] = (delta[existing.key] || 0) + remaining; zeroAfter.push({ key: existing.key, op: { type: 'change', line: l, quantity: 0 } }); return; }
      var taggedAnchor = lines.find(function (a) { return a !== l && !isGift(a) && tagOf(a) && candidates.some(function (r) { return r.ref === tagOf(a) && has(r.variantIds, a.variant_id); }); });
      var rule2 = (taggedAnchor && byRef[tagOf(taggedAnchor)]) || candidates.find(function (r) { return r.giftIds.length; }) || candidates[0];
      var item = { id: l.variant_id, quantity: remaining, properties: tagProps(l.properties, rule2) };
      if (l.selling_plan_allocation) item.selling_plan = l.selling_plan_allocation.selling_plan.id;
      /* tagged copy first, then empty the untagged line (only if the copy was added) */
      var addOp = { type: 'add', item: item };
      ops.push(addOp);
      ops.push({ type: 'change', line: l, quantity: 0, dependsOn: addOp });
    });
    /* 2c. apply the accumulated quantity changes to the lines that absorbed units (or donated the gift unit) */
    var deltaOps = {};
    Object.keys(delta).forEach(function (key) {
      var l = lines.find(function (x) { return x.key === key; });
      if (!l || removed[key]) return;
      var q = l.quantity + delta[key];
      if (q !== l.quantity) { deltaOps[key] = { type: 'change', line: l, quantity: Math.max(0, q), dependsOn: dependsOn[key] || null }; ops.push(deltaOps[key]); }
    });
    zeroAfter.forEach(function (z) { z.op.dependsOn = deltaOps[z.key] || null; ops.push(z.op); });

    return { ops: ops, notes: notes };
  }

  /* ---------- execute, re-resolving each line from a fresh cart ---------- */
  function resolve(cart, line) {
    var items = cart.items || [];
    return items.find(function (l) { return l.key === line.key; }) || items.find(function (l) { return sig(l) === sig(line); }) || null;
  }
  function execute(ops) {
    return ops.reduce(function (chain, op) {
      return chain.then(function () {
        if (op.dependsOn && !op.dependsOn.__ok) { console.warn(LOG, 'skipped: the step it depends on failed'); return null; }
        if (op.type === 'add') return post('cart/add.js', { items: [op.item] }).then(function (res) { op.__ok = !!(res && res.ok); return res; });
        return getCart().then(function (cart) {
          var l = resolve(cart, op.line);
          if (!l) return null;
          var body = { id: l.key, quantity: op.quantity };
          if (op.properties) body.properties = op.properties;
          if ('selling_plan' in op) body.selling_plan = op.selling_plan;
          return post('cart/change.js', body).then(function (res) { op.__ok = !!(res && res.ok); return res; });
        });
      }).catch(function (e) { console.warn(LOG, 'step failed', e); });
    }, Promise.resolve());
  }

  /* ---------- drawer / page refresh and the note ---------- */
  function refresh(notes) {
    try { if (window.Alpine && Alpine.store('xMiniCart')) Alpine.store('xMiniCart').reLoad(); } catch (e) {}
    if (!notes.length) return;
    var text = notes.filter(function (n, i, a) { return a.indexOf(n) === i; }).join(' ');
    var tries = 0;
    (function place() {
      /* In the drawer the note sits above the scrolling column, so a shopper scrolled to the bottom still sees it. */
      var host = document.querySelector('#CartDrawer .aaa-cd-items, #CartDrawer .cart-items, #CartDrawer form, #main-cart-items');
      if (!host || (window.Alpine && Alpine.store('xMiniCart') && Alpine.store('xMiniCart').loading)) {
        if (tries++ < 20) return setTimeout(place, 150);
        return;
      }
      var old = document.querySelector('.aaa-cart-note'); if (old) old.remove();
      var p = document.createElement('p');
      p.className = 'aaa-cart-note'; p.setAttribute('role', 'status'); p.textContent = text;
      host.parentNode.insertBefore(p, host);
      setTimeout(function () { if (p.parentNode) p.remove(); }, 9000);
    })();
  }

  /* The rendered lines (key + quantity) no longer match the cart: Appstle's pass split, merged or re-keyed lines after
     the theme had already re-rendered. A line the shopper is typing in is left alone. */
  function domStale(cart) {
    try {
      if (window.Alpine && Alpine.store('xMiniCart') && Alpine.store('xMiniCart').loading) return false;
      var inputs = document.querySelectorAll('#CartDrawer .cart-qty, #main-cart-items .cart-qty');
      if (!inputs.length) return false;
      var dom = {};
      Array.prototype.forEach.call(inputs, function (i) { if (i !== document.activeElement) dom[i.id.replace(/^cart-qty-/, '')] = Number(i.value); });
      var live = {};
      (cart.items || []).forEach(function (l) { live[l.key] = l.quantity; });
      var dk = Object.keys(dom).sort(), lk = Object.keys(live).sort();
      if (document.activeElement && /^cart-qty-/.test(document.activeElement.id || '')) lk = lk.filter(function (k) { return k !== document.activeElement.id.replace(/^cart-qty-/, ''); });
      if (dk.join() !== lk.join()) return true;
      return dk.some(function (k) { return dom[k] !== live[k]; });
    } catch (e) { return false; }
  }

  /* ---------- scheduling ---------- */
  var timer = null, running = false, again = false;
  function run() {
    if (running) { again = true; return; }
    running = true;
    var R = rules();
    var notes = [], passes = 0, lastCart = null;
    function pass() {
      return getCart().then(function (cart) {
        lastCart = cart;
        var p = plan(cart, R);
        notes = notes.concat(p.notes);
        if (!p.ops.length || passes >= 2) return false;
        passes++;
        console.log(LOG, 'pass', passes, p.ops.map(function (o) { return o.type + (o.line ? ' ' + o.line.variant_id + ' q' + o.quantity : ' ' + o.item.id); }));
        return execute(p.ops).then(pass);
      });
    }
    loadGiftPlans(R).then(pass).catch(function (e) { console.warn(LOG, e); }).then(function () {
      if (passes > 0) refresh(notes);
      else if (lastCart && domStale(lastCart)) { console.log(LOG, 'drawer out of date, re-rendering'); refresh([]); }
      running = false;
      if (again) { again = false; schedule(300); }
    });
  }
  function schedule(ms) {
    clearTimeout(timer);
    timer = setTimeout(run, ms == null ? 1200 : ms);
  }

  document.addEventListener('eurus:cart:items-changed', function () { schedule(); });
  window.addEventListener('appstle-bundles:reconcile', function (e) { if (e.detail && e.detail.state === 'done') schedule(400); });
  /* Any cart write the page makes (theme, apps) that is not one of ours */
  var realFetch = window.fetch;
  window.fetch = function (input, init) {
    var p = realFetch.apply(this, arguments);
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (!(init && init.__internalCall) && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url) && (!init || !init.method || String(init.method).toUpperCase() === 'POST')) {
        p.then(function () { schedule(); }, function () {});
      }
    } catch (e) {}
    return p;
  };
  if (/^\/cart\/?$/.test(location.pathname)) schedule(800);
})();
